// The `Cookies` imported from `react-cookie` is actually from `universal-cookie` API.
// This API is a bit counter-intuitive in which the `get` function returns `any`
// which is not recommended.
// Instead, it should return `T | undefined` or `unknown`.
// Therefore, we're wrapping it in a more convenient API,
// in which we can get the value with a type-safe way. (e.g. `getCheckoutIdCookie()`)

// [Rule of thumb]
// - If cookie value is a primitive type (string, number, boolean), `get` should return the type of the value
// - If cookie value is an array, `get` should return `Array<unknown>`
// - If cookie value is an object, `get` should return `Record<string, unknown>` or `unknown`
// - Optional values (a.k.a. `T | undefined`) is up to you.
// - The reason for `unknown` is to avoid coupling to other datatypes.

import { Cookies } from "react-cookie";
import { getTamaraSharedCookieAttributes } from "./shared-domain";

export const COOKIE_KEY_CHECKOUT_ID = "tamara-checkout-id";
export const UNDEFINED_CHECKOUT_ID = "unknown-checkout-id";

export const COOKIE_KEY_CHECKOUT_TOKEN = "tamara-checkout-token";
export const UNDEFINED_CHECKOUT_TOKEN = "unknown-checkout-token";

export const COOKIE_KEY_CHECKOUT_USER_ID = "tamara-checkout-user-id";
export const UNDEFINED_CHECKOUT_USER_ID = "unknown-checkout-user-id";

export const COOKIE_KEY_CHECKOUT_SEON = "tamara-checkout-seon";
export const UNDEFINED_CHECKOUT_SEON = "unknown-checkout-seon";

export const COOKIE_KEY_DEVICE_ID = "tamara-customer-device-id";
export const COOKIE_KEY_CHECKOUT_LOCALE = "tamara-checkout-locale";

const cookies = new Cookies();

export function getDeviceIdCookie() {
  return cookies.get(COOKIE_KEY_DEVICE_ID) as string | undefined;
}

export function getCheckoutIdCookie() {
  return cookies.get(COOKIE_KEY_CHECKOUT_ID) as string | undefined;
}

export function removeCheckoutIdCookie() {
  cookies.remove(COOKIE_KEY_CHECKOUT_ID, { path: "/" });
  // Also remove the parent-domain cookie set by the checkout repo
  // (checkout sets it with domain=tamara.co so it leaks to all subdomains)
  cookies.remove(COOKIE_KEY_CHECKOUT_ID, { path: "/", domain: ".tamara.co" });
  cookies.remove(COOKIE_KEY_CHECKOUT_ID, { path: "/", domain: ".tamaratech.co" });
}

export function getCheckoutTokenCookie() {
  return cookies.get(COOKIE_KEY_CHECKOUT_TOKEN) as string | undefined;
}

/**
 * Retrieves the auth token from cookies first, falling back to sessionStorage.
 * In embedded environments (iframes/webviews) where third-party cookies are
 * blocked, the checkout app stores the token in sessionStorage. After the
 * same-origin migration (checkout.tamara.co/v2), both apps share the same
 * origin so sessionStorage is accessible.
 */
export function getCheckoutToken(): string | undefined {
  const fromCookie = getCheckoutTokenCookie();
  if (fromCookie) return fromCookie;

  try {
    return sessionStorage.getItem(COOKIE_KEY_CHECKOUT_TOKEN) ?? undefined;
  } catch {
    return undefined;
  }
}

export function getCheckoutSeonCookie() {
  return cookies.get(COOKIE_KEY_CHECKOUT_SEON) as string | undefined;
}

export function setCheckoutSeonCookie(seonId: string) {
  return cookies.set(COOKIE_KEY_CHECKOUT_SEON, seonId);
}

export function getCheckoutUserIdCookie() {
  return cookies.get(COOKIE_KEY_CHECKOUT_USER_ID) as string | undefined;
}

export function setCheckoutUserIdCookie(userId: string) {
  return cookies.set(COOKIE_KEY_CHECKOUT_USER_ID, userId, {
    ...getTamaraSharedCookieAttributes(),
    maxAge: 31536000,
  });
}

/** Share auth token on the parent Tamara domain for cross-app redirects. */
export function setCheckoutTokenHandoffCookie(token: string) {
  return cookies.set(COOKIE_KEY_CHECKOUT_TOKEN, token, {
    ...getTamaraSharedCookieAttributes(),
    maxAge: 31536000,
  });
}

export function setCheckoutLocaleCookie(locale: string) {
  return cookies.set(COOKIE_KEY_CHECKOUT_LOCALE, locale, {
    ...getTamaraSharedCookieAttributes(),
    maxAge: 31536000,
  });
}

/**
 * Reads a cookie value from document.cookie manually.
 * This is a fallback when react-cookie fails or is unavailable.
 */
function readCookieFromDocument(cookieName: string): string | undefined {
  const cookieString = document.cookie;
  if (!cookieString) {
    return undefined;
  }

  const cookieParts = cookieString.split(";");

  const foundCookie = cookieParts.find((cookie) => {
    const trimmedCookie = cookie.trim();
    const equalIndex = trimmedCookie.indexOf("=");
    if (equalIndex === -1) {
      return false;
    }
    const name = trimmedCookie.slice(0, equalIndex);
    return name === cookieName;
  });

  if (!foundCookie) {
    return undefined;
  }

  const trimmedCookie = foundCookie.trim();
  const equalIndex = trimmedCookie.indexOf("=");
  const value = trimmedCookie.slice(equalIndex + 1).trim();

  if (!value) {
    return undefined;
  }

  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

export function getCheckoutLocaleCookie() {
  try {
    const cookieValue = cookies.get(COOKIE_KEY_CHECKOUT_LOCALE) as
      | string
      | undefined;
    if (cookieValue) {
      return cookieValue;
    }
  } catch {
    // Fall through to document.cookie read
  }

  return readCookieFromDocument(COOKIE_KEY_CHECKOUT_LOCALE);
}
